import React, { useEffect, useRef, useState } from 'react';
import VideoService from '../../../../../../service/VideoService';
import styles from './TextPanels.module.scss';
import Icon from '../../../../../../components/Icon/Icon';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../../../../common/components/Spinner';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import { EXAMPLE_BOTTOM_TEXT, EXAMPLE_TOP_TEXT } from '../../constants';
import { useUserContext } from '../../../../../../context/UserContext';
import { prepareTextPanelsList } from '../../utils';
import DotsBtn from 'engage/components/Menu/DotsBtn';
import Menu from 'engage/components/Menu/Menu';
import MenuItem from 'engage/components/Menu/MenuItem';
import ScrollContainer from 'engage/components/ScrollContainer/ScrollContainer';
import useFixedCardMenu from 'engage/features/Editor/hooks/useFixedCardMenu';
import { useAccordion } from 'engage/routes/CompanySettings/hooks/useAccordion';
import { BrandingAccordionHeader } from '../../../BrandingAccordionHeader/BrandingAccordionHeader';

const TextPanels = ({ handleOpenEditor, companyId }) => {
  const [textPanelsList, setTextPanelsList] = useState([]);
  const [selectedTextPanelId, setSelectedTextPanelId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const scrollContainerRef = useRef(null);

  const { isActive, toggleAccordionState, contentRef } = useAccordion(true);
  const { menuRef, toggleShowMenu, showMenu, menuCoordinates, onOpenMenu } = useFixedCardMenu({ containerRef: scrollContainerRef, offsetX: 200, offsetY: 300 });

  const { updateUserAppConfig } = useUserContext();

  const { t } = useTranslation();

  const getTextPanelsList = async () => {
    try {
      setIsLoading(true);
      const result = await VideoService.getTextPanels(companyId);
      if (result.data) {
        const preparedList = await prepareTextPanelsList(result.data);

        setTextPanelsList(preparedList);

        const defaultTextPanel = preparedList.find(({ active }) => active);
        setSelectedTextPanelId(defaultTextPanel?.id);
      }
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
  };

  const handleChangeDefaultTextPanel = async id => {
    try {
      setIsLoading(true);
      await VideoService.updateTextPanel(id, { active: true }, companyId);

      ToastNotification({
        message: t('company.settings.text_panels.change_default.success'),
        iconName: 'success',
      });

      await getTextPanelsList();
      updateUserAppConfig();
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.text_panels.change_default.error'),
      });
    }

    setIsLoading(false);
  };

  const handleDeleteTextPanel = async textPanelId => {
    try {
      const isConfirm = confirm(t('company.settings.text_panels.delete.confirmation'));

      if (!isConfirm) {
        return;
      }
      setIsLoading(true);
      await VideoService.deleteTextPanel(textPanelId, companyId);

      ToastNotification({
        message: t('company.settings.text_panels.delete.success'),
        iconName: 'success',
      });

      await getTextPanelsList();
      updateUserAppConfig();
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.text_panels.delete.error'),
      });
    }

    setIsLoading(false);
  };

  const handleDuplicateTextPanel = async textPanelId => {
    try {
      setIsLoading(true);

      const textPanelData = await VideoService.getTextPanel(textPanelId, companyId);

      if (textPanelData) {
        const originalPanel = textPanelData;

        const { spacing, marginX, marginY, position, title, type, bgColor, textPadding } = originalPanel;

        let top, bottom;

        if (type === 0) { // ONE_PANEL type
          top = { ...originalPanel.labelTop };
          bottom = { ...originalPanel.labelBottom };

          if (top?.font) {
            top.fontId = top.font.id;
          }

          if (bottom?.font) {
            bottom.fontId = bottom.font.id;
          }

          top.backgroundColor = bgColor;
          top.textPadding = textPadding;
        } else { // TWO_PANELS type
          top = { ...originalPanel.panelTop };
          bottom = { ...originalPanel.panelBottom };

          if (top?.font) {
            top.fontId = top.font.id;
          }

          if (bottom?.font) {
            bottom.fontId = bottom.font.id;
          }
        }

        const duplicateTitle = `${title} - Copy`;

        const payload = {
          title: duplicateTitle,
          type,
          position,
          marginX,
          marginY,
          spacing,
          panelTop: top,
          panelBottom: bottom,
          default: false
        };

        await VideoService.createTextPanel(payload, companyId);

        ToastNotification({
          message: t('company.settings.text_panels.duplicate.success'),
          iconName: 'success',
        });

        await getTextPanelsList();
        updateUserAppConfig();
      }
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.text_panels.duplicate.error'),
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    getTextPanelsList();
    scrollContainerRef.current?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
  }, [companyId]);

  return (
    <div className={styles.mainContainer}>
      <BrandingAccordionHeader isActive={isActive} onClick={toggleAccordionState} title={`${t('company.settings.text_panels.title')} (${textPanelsList.length})`} description={t('company_settings.branding.text_panels_description')} />
      <div ref={contentRef} style={{ overflow: 'hidden', position: 'relative' }}>

        <ScrollContainer
          innerRef={scrollContainerRef}
        className={cx(styles.textPanelsListContainer, { [styles.textPanelsListDisabled]: isLoading })}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleOpenEditor('new')}
          disabled={isLoading}
          className={styles.textPanelItem}
        >
          <div className={cx(styles.textPanelContainer, styles.newTextPanelBtn)}>
            <Icon src={'plus'} alt={t('logo_chooser.header')} />
            <span className={styles.newLogoBtn}>{t('company.settings.text_panels.new_text_panel')}</span>
          </div>
        </div>
        {textPanelsList.map((textPanel) => (
          <div
            key={textPanel?.id}
            className={cx(styles.textPanelItem, { [styles.textPanelItemActive]: textPanel?.id === selectedTextPanelId })}
          >
            <div className={styles.textPanelContainer} role='button' tabIndex={0} onClick={() => handleOpenEditor(textPanel?.id)}>
              <div className={cx(styles.container)}>
                <div className={styles.textPanelTextContainer} style={textPanel.containerStyles}>
                  <div style={textPanel.topLabelStyles}>{EXAMPLE_TOP_TEXT}</div>
                  <div style={textPanel.bottomLabelStyles}>{EXAMPLE_BOTTOM_TEXT}</div>
                </div>
                <div className={styles.infoContainer}>
                  <DotsBtn className={styles.dotsBtn} onClick={(event) => { event.stopPropagation(); onOpenMenu({ id: textPanel?.id, event }) }} />
                  {textPanel?.id === selectedTextPanelId && <span className={styles.defaultLabel}>{t('company.settings.fonts.default')}</span>}
                </div>
              </div>
              <div className={styles.textPanelTitle}>{textPanel?.title}</div>
            </div>
          </div>
        ))}
        </ScrollContainer>
        {isLoading && isActive && <div className={styles.spinnerContainer}>
          <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />
        </div>}
      </div>
      {showMenu.flag && <Menu ref={menuRef}
        customStyles={{
          width: 'fit-content',
          top: menuCoordinates.top,
          left: menuCoordinates.left,
          transform: menuCoordinates.transform,
        }}>
        <MenuItem onClick={() => { toggleShowMenu(); handleChangeDefaultTextPanel(showMenu.id) }}>
          <Icon src='circleCheck' className={styles.actionIcon} />
          <span>{t('company_settings.branding.text_panels.make_default')}</span>
        </MenuItem>
        <MenuItem onClick={() => { toggleShowMenu(); handleDuplicateTextPanel(showMenu.id) }}>
          <Icon src="duplicate_generic" className={styles.actionIcon} /> <span>{t('general.video_action_button.duplicate')}</span>
        </MenuItem>
        <MenuItem onClick={() => { toggleShowMenu(); handleDeleteTextPanel(showMenu.id) }}>
          <Icon src="bin_red" className={styles.binActionIcon} /> <span style={{ color: 'rgb(203, 0, 0)' }}>{t('general.video_action_button.delete')}</span>
        </MenuItem>
      </Menu>}
    </div>
  );
};

export default TextPanels;
